import { Money } from '@commercetools/platform-sdk';
import { CartInStore } from '../../slices/cart/types';
import {
  WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
} from '../../utils/common/constants';
import { ShopPayPaymentRequest } from '../../hooks/useShopPaySession/types';

const locale = 'en-US';

const calculateTotalGiftCards = ({
  giftCards = [],
}: {
  giftCards?: { amount: Money }[];
}) => giftCards?.reduce((acc, giftCard) => acc + giftCard.amount.centAmount, 0);

const buildDiscounts = ({ cart }: { cart: CartInStore }) => {
  const { giftCards = [] } = cart;

  if (!giftCards.length) {
    return 0;
  }

  return calculateTotalGiftCards(cart);
};

const buildTotal = ({ cart }: { cart: CartInStore }) => {
  const totalGiftCards = calculateTotalGiftCards(cart);

  const cartTotal =
    cart.taxedPrice?.totalGross.centAmount ?? cart.totalPrice.centAmount;

  const total = cartTotal - totalGiftCards;

  return total <= 0 ? 0 : total;
};

const convertCartToShopPay = (cart: CartInStore): ShopPayPaymentRequest => {
  const total = buildTotal({ cart });
  const discounts = buildDiscounts({ cart });
  const warrantyCustomLineItems =
    cart.customLineItems
      ?.filter(
        (customLineItem) =>
          customLineItem.custom?.fields?.type ===
          WARRANTY_CUSTOM_LINE_ITEM_TYPE,
      )
      .map((lineItem) => ({
        label: lineItem.name?.[locale],
        quantity: lineItem.quantity,
        finalItemPrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        finalLinePrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        originalItemPrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        originalLinePrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        lineDiscounts: [],
      })) ?? [];

  const recyclingFeeCustomLineItems =
    cart?.customLineItems
      ?.filter(
        (customLineItem) => customLineItem?.slug === 'custom_recyclingFee',
      )
      ?.map((lineItem) => ({
        label: lineItem.name?.[locale],
        quantity: lineItem.quantity,
        finalItemPrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        finalLinePrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        originalItemPrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        originalLinePrice: {
          amount:
            (lineItem.money?.centAmount ?? 0) /
            10 ** (lineItem?.money?.fractionDigits ?? 0),
          currencyCode: lineItem.money.currencyCode,
        },
        lineDiscounts: [],
      })) ?? [];

  const deliveryFeeLineItems =
    cart?.customLineItems
      ?.filter(
        (customLineItem) =>
          customLineItem.custom?.fields?.type ===
          DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
      )
      ?.map((lineItem) => {
        const lineItemName = lineItem.name?.[locale] ?? '';
        return {
          label: lineItemName,
          quantity: lineItem.quantity,
          finalItemPrice: {
            amount:
              (lineItem.money?.centAmount ?? 0) /
              10 ** (lineItem?.money?.fractionDigits ?? 0),
            currencyCode: lineItem.money.currencyCode,
          },
          finalLinePrice: {
            amount:
              (lineItem.money?.centAmount ?? 0) /
              10 ** (lineItem?.money?.fractionDigits ?? 0),
            currencyCode: lineItem.money.currencyCode,
          },
          originalItemPrice: {
            amount:
              (lineItem.money?.centAmount ?? 0) /
              10 ** (lineItem?.money?.fractionDigits ?? 0),
            currencyCode: lineItem.money.currencyCode,
          },
          originalLinePrice: {
            amount:
              (lineItem.money?.centAmount ?? 0) /
              10 ** (lineItem?.money?.fractionDigits ?? 0),
            currencyCode: lineItem.money.currencyCode,
          },
          lineDiscounts: [],
        };
      }) ?? [];

  const lineItemsItems = cart.lineItems.map((lineItem) => {
    const productName = lineItem.name?.[locale] ?? '';
    return {
      label: productName,
      quantity: lineItem.quantity,
      sku: lineItem.variant.sku,
      requiresShipping: true,
      image: {
        url: lineItem.variant.images?.[0]?.url ?? '',
        alt: productName,
      },
      finalItemPrice: {
        amount:
          lineItem.price.value.centAmount /
          10 ** lineItem.price.value.fractionDigits,
        currencyCode: lineItem.price.value.currencyCode,
      },
      originalItemPrice: {
        amount:
          lineItem.price.value.centAmount /
          10 ** lineItem.price.value.fractionDigits,
        currencyCode: lineItem.price.value.currencyCode,
      },
      originalLinePrice: {
        amount:
          lineItem.totalPrice.centAmount /
          10 ** lineItem.totalPrice.fractionDigits,
        currencyCode: lineItem.totalPrice.currencyCode,
      },
      finalLinePrice: {
        amount:
          lineItem.totalPrice.centAmount /
          10 ** lineItem.totalPrice.fractionDigits,
        currencyCode: lineItem.totalPrice.currencyCode,
      },
      lineDiscounts: [],
    };
  });

  const totalAmount = total / 10 ** cart.totalPrice.fractionDigits;

  const taxAmount = cart.taxedPrice?.taxPortions
    ? cart.taxedPrice.taxPortions.reduce(
        (totalTaxAmount, taxPortion) =>
          totalTaxAmount + taxPortion.amount.centAmount,
        0,
      ) /
      10 ** (cart.taxedPrice.totalGross?.fractionDigits ?? 2)
    : 0;

  const subtotalAmount = totalAmount - taxAmount;

  const items = [
    ...lineItemsItems,
    ...recyclingFeeCustomLineItems,
    ...deliveryFeeLineItems,
    ...warrantyCustomLineItems,
  ];

  const shopPayRequestInput = {
    lineItems: items,
    discountCodes: [],
    // TODO: It's hardcoded for now because shopPay doesn't support our shipping methods strategy
    deliveryMethods: [
      {
        amount: { amount: 0, currencyCode: 'USD' },
        code: 'STANDARD',
        label: 'Standard shipping',
      },
    ],
    subtotal: {
      amount: subtotalAmount,
      currencyCode: cart.totalPrice.currencyCode,
    },
    totalTax: {
      amount: taxAmount,
      currencyCode: cart.totalPrice.currencyCode,
    },
    total: {
      amount: totalAmount,
      currencyCode: cart.totalPrice.currencyCode,
    },
    presentmentCurrency: 'USD' as const,
    locale: 'en',
    // TODO: It's hardcoded for now because shopPay doesn't support our shipping methods strategy
    shippingLines: [
      {
        amount: { amount: 0, currencyCode: 'USD' },
        code: 'STANDARD',
        label: 'Standard shipping',
      },
    ],

    discounts:
      discounts > 0
        ? [
            {
              amount: {
                amount: discounts,
                currencyCode: cart.totalPrice.currencyCode,
              },
              label: 'Gift Card',
            },
          ]
        : [],
  };

  return shopPayRequestInput;
};

const shopPayService = {
  convertCartToShopPay,
};

export default shopPayService;
